/* eslint-disable @nice-cxone/ccf/required-tsdoc */
import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  TextField,
  Button,
  Container,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  InputAdornment,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  Delete,
  Replay,
  AccessTime,
  CheckCircle,
  Cancel,
  FileUpload,
  Download,
  Downloading,
  Edit,
  Check,
  Close,
  ExpandMore,
  Visibility,
  VisibilityOff,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { FormData, AssociatedFile } from './admin';
import classes from './verticalForm.module.scss';
import {
  API_BASE_URL,
  EXPERT_CONVERSATIONS,
  EXPERT_CREATE,
  EXPERT_UPDATE,
  AUTHORIZATION_TOKEN,
  callApi,
  DOWNLOAD_CONVERSATION_FILES,
  RENAME_CONVERSATION_FILE,
  TEST_EXPERT,
  UPDATE_FILE_STATUS,
} from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-api';
import { notification } from 'antd';
import { ToastContainer, toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import { v4 as uuidv4 } from 'uuid';

interface VerticalFormProps {
  formData: FormData;
  handleBack: () => void;
  handleFormDataChange: (name: string, value: string | AssociatedFile[]) => void;
  mode?: 'create' | 'edit';
  setIsFormVisible: (isFormVisible: boolean) => void;
  setRows: (rows: FormData[]) => void;
  rows: FormData[];
  setOriginalRows: (originalRows: FormData[]) => void;
  originalRows: FormData[];
}

interface FileWrapper {
  id: string;
  file: File;
  status: 'uploading' | 'success' | 'error';
  error: string | null;
}

export function VerticalForm({
  formData,
  handleBack,
  handleFormDataChange,
  mode = 'create',
  setIsFormVisible,
  setRows,
  rows,
  setOriginalRows,
  originalRows,
}: VerticalFormProps): JSX.Element {

  const defaultFormData = {
    ...formData,
    visibility: formData.visibility || 'Private',
    status: formData.status || 'Active',
  };

  formData = defaultFormData;
  const isCreateMode = mode === 'create';
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({ key: false, secret: false });
  const [downloadingFiles, setDownloadingFiles] = useState<{ [key: string]: boolean }>({});
  const [newFileNames, setNewFileNames] = useState<{ [key: string]: string }>({});
  const [newFileDescriptions, setNewFileDescriptions] = useState<{ [key: string]: string }>({});
  const [openModal, setOpenModal] = useState(false);
  const [openEditFileModal, setOpenEditFileModal] = useState<{ [key: string]: boolean }>({});
  const [answers, setAnswers] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const { accounts } = useMsal();
  const loggedInUser = accounts[0]?.username?.toLowerCase();
  const currentDateTime = new Date().toISOString();
  const formatUsername = (email: string) => {
    const [firstName, lastName] = email.split('@')[0].split('.');
    return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
  };

  const handleClickShowPassword = (field: string) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const handleOpenEditFileModal = (id: string) => {
    const fileIndex = files.findIndex((file) => file.id === id);
    setNewFileNames((prev) => ({ ...prev, [id]: formData.conversationDetails[fileIndex].name }));
    setOpenEditFileModal((prev) => ({ ...prev, [id]: true }));
  };

  const handleCloseEditFileModal = (id: string) => {
    setOpenEditFileModal((prev) => ({ ...prev, [id]: false }));
  };

  const handleRenameSave = async (id: string) => {
    const fileIndex = files.findIndex((file) => file.id === id);
    const fileName = formData.conversationDetails[fileIndex].name;
    const newFileName = newFileNames[id];
    const newFileDescription = newFileDescriptions[id] !== undefined ? newFileDescriptions[id] : formData.conversationDetails[fileIndex].description;

    const updatedFileDescription = formData.conversationDetails.map((file: AssociatedFile, i: number) =>
      i === fileIndex ? { ...file, description: newFileDescription, lastUpdatedBy: loggedInUser, lastUpdatedTime: currentDateTime } : file
    );
    handleFormDataChange('conversationDetails', updatedFileDescription);

    try {
      const response = await fetch(`${API_BASE_URL}${RENAME_CONVERSATION_FILE}`, {
        method: 'POST',
        headers: {
          'Authorization': AUTHORIZATION_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oldFileName: `${fileName}.json`,
          newFileName: `${newFileName}.json`
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to rename file');
      }

      setNewFileNames((prev) => {
        const updatedFileNames = { ...prev };
        delete updatedFileNames[id];
        updatedFileNames[id] = newFileName;
        return updatedFileNames;
      });

      setFiles((prevFiles) =>
        prevFiles.map((fileWrapper) =>
          fileWrapper.id === id
            ? { ...fileWrapper, file: new File([fileWrapper.file], newFileName) }
            : fileWrapper
        )
      );

      const newConversationDetails = formData.conversationDetails.map((file: AssociatedFile, i: number) =>
        i === fileIndex ? { ...file, name: newFileName, description: newFileDescription, lastUpdatedBy: loggedInUser, lastUpdatedTime: currentDateTime } : file
      );
      handleFormDataChange('conversationDetails', newConversationDetails);

      setOpenEditFileModal((prev) => ({ ...prev, [id]: false }));

    } catch (error) {
      console.error('Error renaming file:', error);
    }
  };

  const handleFileNameChange = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFileNames((prev) => ({ ...prev, [id]: e.target.value }));
  };

  const handleDescriptionChange = (id: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewFileDescriptions((prev) => ({ ...prev, [id]: e.target.value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    const fieldValue = files
      ? Array.from(files).map((file) => ({
        name: file.name,
        description: '',
        lastUpdatedBy: loggedInUser,
        lastUpdatedTime: currentDateTime,
        status: 'Active',
      }))
      : value;
    handleFormDataChange(name, fieldValue);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const transformedData = {
        associatedFiles: formData.conversationDetails.map(
          ({ name, description, lastUpdatedBy, lastUpdatedTime, status }: AssociatedFile) => ({
            name,
            description,
            lastUpdatedBy,
            lastUpdatedTime,
            status,
          })
        ),
        compliancePath: formData.compliancePath,
        expertCopilotURL: formData.copilotURL,
        expertPathName: formData.expertName,
        id: formData.id,
        expertCopilotClientKey: formData.key,
        kBAnswersPath: formData.knowledgePath,
        expertSystem: formData.name,
        persona: formData.persona,
        salesOffersPath: formData.salesPath,
        expertCopilotClientSecret: formData.secret,
        status: formData.status,
        demoType: formData.demoType,
        visibility: formData.visibility,
        owner: isCreateMode ? formatUsername(loggedInUser) : formData.owner,
        description: formData.description,
        lastUpdatedBy: loggedInUser,
        lastUpdatedTime: currentDateTime,
      };

      const apiEndpoint = isCreateMode
        ? `${API_BASE_URL}${EXPERT_CREATE}`
        : `${API_BASE_URL}${EXPERT_UPDATE}`;

      const createExpertResponse = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          Authorization: AUTHORIZATION_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
      });

      if (!createExpertResponse.ok) {
        const errorText = await createExpertResponse.text();
        const error = isCreateMode
          ? 'Failed to create demo:'
          : 'Failed to update demo:';
        throw new Error(`${error} ${errorText}`);
      }

      const allFilesUploaded = files.every((file) => file.status === 'success');
      if (!allFilesUploaded) {
        throw new Error('Files are not uploaded successfully');
      }

      const updatedFormData = {
        ...formData,
        conversationDetails: transformedData.associatedFiles.map(
          ({ name, description, lastUpdatedBy, lastUpdatedTime, status }: AssociatedFile) => ({
            name,
            description,
            lastUpdatedBy,
            lastUpdatedTime,
            status,
          })
        ),
        lastUpdatedBy: transformedData.lastUpdatedBy,
        lastUpdatedTime: transformedData.lastUpdatedTime,
        ...(isCreateMode && {
          owner: transformedData.owner,
        }),
      };

      if (isCreateMode) {
        const newDemo = { ...updatedFormData, id: rows.length + 1 };
        const newRows = [...rows, newDemo];
        setRows(newRows);
        setOriginalRows(newRows);      
      } else {
        const updatedRows = rows.map((row) =>
          row.id === formData.id ? updatedFormData : row
        );
        setRows(updatedRows);
        setOriginalRows(updatedRows);
      }
      notification.success({
        message: isCreateMode ? 'Demo Created' : 'Demo Updated',
        description: isCreateMode
          ? 'The demo has been successfully created.'
          : 'The demo has been successfully updated.',
      });
      setIsFormVisible(false);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      notification.error({
        message: 'Error',
        description: error.message,
      });
    }
  };

  const [files, setFiles] = useState<FileWrapper[]>([]);
  const sortedFileArray = [...files].sort((a, b) =>
    a.file.name.toLowerCase().localeCompare(b.file.name.toLowerCase())
  );


  const updateFileStatus = useCallback(
    (
      index: number,
      status: 'uploading' | 'success' | 'error',
      error: string | null = null
    ): void => {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        if (updatedFiles[index]) {
          updatedFiles[index].status = status;
          updatedFiles[index].error = error;
        }
        return updatedFiles;
      });
    },
    []
  );

  const handleDelete = (id: string): void => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));

    const newConversationDetails = formData.conversationDetails.filter(
      (_, i) => files[i].id !== id
    );
    handleFormDataChange('conversationDetails', newConversationDetails);
  };

  const handleRetry = (id: string): void => {
    const fileIndex = files.findIndex((file) => file.id === id);
    updateFileStatus(fileIndex, 'uploading', null);
    handleUpload(files[fileIndex].file, fileIndex);
  };

  const handleUpload = useCallback(
    async (file: File, index: number): Promise<void> => {
      try {
        updateFileStatus(index, 'uploading');

        const fileContent = await file.text();

        try {
          JSON.parse(fileContent);
        } catch (error) {
          throw new Error('Invalid JSON format, Please check the file content');
        }

        const conversationData = [
          {
            name: file.name,
            content: {
              data: fileContent,
            },
          },
        ];

        const response = await fetch(`${API_BASE_URL}${EXPERT_CONVERSATIONS}`, {
          method: 'POST',
          headers: {
            Authorization: AUTHORIZATION_TOKEN,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(conversationData),
        });

        if (response.ok) {
          updateFileStatus(index, 'success');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Upload failed');
        }
      } catch (error: any) {
        updateFileStatus(index, 'error', error.message);
      }
    },
    [updateFileStatus]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: FileWrapper[] = acceptedFiles.map((file) => ({
        id: uuidv4(),
        file,
        status: 'uploading',
        error: null,
      }));
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);

      const acceptedFileNames = acceptedFiles.map((file) => {
        const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        return new File([file], fileNameWithoutExtension);
      });

      handleFormDataChange('conversationDetails', [
        ...formData.conversationDetails,
        ...acceptedFileNames.map((file) => ({
          name: file.name,
          description: '',
          lastUpdatedBy: loggedInUser,
          lastUpdatedTime: currentDateTime,
          status: 'Active',
        })),
      ]);

      newFiles.forEach((fileWrapper, index) =>
        handleUpload(fileWrapper.file, files.length + index)
      );
    },
    [currentDateTime, files.length, formData.conversationDetails, handleFormDataChange, handleUpload, loggedInUser]
  );

  const handleDownload = (id: string) => {
    const fileIndex = files.findIndex((file) => file.id === id);
    const fileName = formData.conversationDetails[fileIndex].name;

    setDownloadingFiles((prev) => ({ ...prev, [id]: true }));

    const url = `${API_BASE_URL}${DOWNLOAD_CONVERSATION_FILES}?fileName=${fileName}.json`;
    callApi(url, 'GET')
      .then((response: any) => {
        const jsonData = JSON.stringify(response, null, 2);

        const blob = new Blob([jsonData], {
          type: 'application/json;charset=utf-8',
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        toast.success(`File ${fileName} downloaded successfully.`);
      })
      .catch((error) => {
        console.error('Error fetching conversation details:', error.message);
        toast.error(`Failed to download file ${fileName}.`);
      })
      .finally(() => {
        setDownloadingFiles((prev) => ({ ...prev, [id]: false }));
      });
  };


  useEffect(() => {
    const prepopulatedFiles: FileWrapper[] = formData.conversationDetails.map(
      (file) => ({
        id: uuidv4(),
        file: new File([], file.name),
        status: 'success',
        error: null,
      })
    );
    setFiles(prepopulatedFiles);
  }, [formData.conversationDetails]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'audio/wav': ['.wav'],
    },
  });

  const statusIcons: { [key: string]: JSX.Element } = {
    uploading: <AccessTime className={classes.uploadingIcon} />,
    success: <CheckCircle className={classes.successIcon} />,
    error: <Cancel className={classes.errorIcon} />,
  };

  const validateRequiredFieldsToTestExpert = () => {
    const { copilotURL, key, secret, testQuestion } = formData;
    const requiredFields = { copilotURL, key, secret, testQuestion };
    const missingFields = Object.entries(requiredFields).filter(([_, value]) => !value);

    if (missingFields.length > 0) {
      const fieldLabels: { [key: string]: string } = {
        copilotURL: 'Copilot URL',
        key: 'Key',
        secret: 'Secret',
        testQuestion: 'Test Question'
      };
      const missingFieldNames = missingFields.map(([field]) => fieldLabels[field]).join(', ');
      setErrorMessage(`The following fields are missing: ${missingFieldNames}.\nPlease fill in the details.`);
      setIsSuccess(false);
      setOpenModal(true);
      return false;
    }
    return true;
  };

  const handleTestExpert = async () => {
    if (!validateRequiredFieldsToTestExpert()) return;

    const { copilotURL, key, secret, testQuestion } = formData;
    const params = { url: copilotURL, key, secret, question: testQuestion };

    try {
      const response = await fetch(`${API_BASE_URL}${TEST_EXPERT}`, {
        method: 'POST',
        headers: {
          Authorization: AUTHORIZATION_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();

      if (!data.response) {
        setErrorMessage('Sorry, I could not find an answer. Please verify the question.');
        setIsSuccess(false);
        setOpenModal(true);
        return;
      }

      const parsedData = JSON.parse(data.response);
      if (!response.ok) {
        throw new Error(parsedData.message);
      }

      let chunks: string[] = [];
      if (Array.isArray(parsedData.kernel)) {
        chunks = parsedData.kernel.map((item: any) => item.chunk);
      } else if (typeof parsedData.kernel === 'object') {
        chunks = [parsedData.kernel.chunk];
      } else {
        throw new Error('Invalid response format');
      }

      const normalizeText = (text: string) => text.replace(/\s+/g, ' ').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();
      const uniqueChunks: string[] = chunks.filter((chunk: string, index: number, self: string[]) =>
        self.findIndex((c: string) => normalizeText(c) === normalizeText(chunk)) === index
      );

      setAnswers(uniqueChunks);
      setErrorMessage(null);
      setIsSuccess(true);
      setOpenModal(true);
    } catch (error: any) {
      setErrorMessage(`Failed to get answer: \n${error.message}`);
      setIsSuccess(false);
      setOpenModal(true);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const getModalContentClass = () => {
    return isSuccess ? classes.success : classes.error;
  };

  const handleFilesToggleStatus = async (fileIndex: number) => {
    const file = formData.conversationDetails[fileIndex];
    const newStatus = file.status === 'Active' ? 'Inactive' : 'Active';

    const newConversationDetails = formData.conversationDetails.map((file, i) =>
      i === fileIndex ? { ...file, status: newStatus } : file
    );
    handleFormDataChange('conversationDetails', newConversationDetails);

    try {
      const response = await fetch(`${API_BASE_URL}${UPDATE_FILE_STATUS}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: AUTHORIZATION_TOKEN,
        },
        body: JSON.stringify({
          expertPathName: formData.expertName,
          fileName: file.name,
          status: newStatus,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update file status');
      }

    } catch (error) {
      console.error('Error updating file status:', error);
    }
  };


  return (
    <Container>
      <Helmet>
        <title>CXone Agent Admin</title>
      </Helmet>
      <Button variant="text" onClick={handleBack} sx={{ mt: 2, padding: 0 }}>
       List of Demos &gt;
      </Button>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        fontWeight={700}
        className={classes.formHeader}
      >
        {isCreateMode
          ? 'Create New Demo'
          : `Edit Demo ${formData.name}`}
      </Typography>

      <form onSubmit={handleSubmit} className={classes.verticalForm}>
        <Typography variant="h6" component="h1" gutterBottom fontWeight={700}>
          Enter Demo Information
        </Typography>
        <Divider sx={{ my: 2, borderColor: 'lightGrey' }} />
        <TextField
          name="name"
          label="Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.name}
          onChange={handleChange}
          required={true}
          disabled={!isCreateMode}
        />
        <TextField
          name="persona"
          label="Persona"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={formData.persona || ''}
          onChange={handleChange}
          required={true}
        />
        <TextField
          name="description"
          label="Description"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={formData.description || ''}
          onChange={handleChange}
        />
        <div className={classes.dropdown}>
          <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel id="demoType-label">Demo Type</InputLabel>
          <Select
            labelId="demoType-label"
            name="demoType"
            value={formData.demoType}
              onChange={(e) => handleFormDataChange(e.target.name, e.target.value as string)}
            label="Demo Type"
            required={true}
          >
            <MenuItem value="Custom">Custom</MenuItem>
            <MenuItem value="Vertical">Vertical</MenuItem>
          </Select>
        </FormControl>
          {(isCreateMode || formData.owner === formatUsername(loggedInUser)) && (
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="visibility-label">Visibility</InputLabel>
              <Select
                labelId="visibility-label"
                name="visibility"
                value={formData.visibility}
                onChange={(e) => handleFormDataChange(e.target.name, e.target.value as string)}
                label="Visibility"
              >
                <MenuItem value="Public">Public</MenuItem>
                <MenuItem value="Private">Private</MenuItem>
              </Select>
            </FormControl>
          )}
        <FormControlLabel
          label="Status"
          control={
            <Switch
              checked={formData.status === 'Active'}
              onChange={(e) => handleFormDataChange(e.target.name, e.target.checked ? 'Active' : 'Inactive')}
              name="status"
              color="primary"
            />
          }
        />
        </div>
        <Typography variant="h6" component="h2" gutterBottom>
          Expert Details
        </Typography>
        <TextField
          name="expertName"
          label="Expert Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.expertName}
          onChange={handleChange}
          disabled={!isCreateMode}
          required={true}
        />
        <TextField
          name="key"
          label="Key"
          type={showPassword.key ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.key}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle key visibility"
                  onClick={() => handleClickShowPassword('key')}
                  edge="end"
                >
                  {showPassword.key ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          name="secret"
          label="Secret"
          type={showPassword.secret ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.secret}
          onChange={handleChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle secret visibility"
                  onClick={() => handleClickShowPassword('secret')}
                  edge="end"
                >
                  {showPassword.secret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          name="copilotURL"
          label="Copilot URL"
          type="text"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.copilotURL}
          onChange={handleChange}
        />
        <TextField
          name="salesPath"
          label="Sales Path"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.salesPath}
          onChange={handleChange}
        />
        <TextField
          name="compliancePath"
          label="Compliance Path"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.compliancePath}
          onChange={handleChange}
        />
        <TextField
          name="knowledgePath"
          label="Knowledge Path"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.knowledgePath}
          onChange={handleChange}
        />
        <TextField
          name="testQuestion"
          label="Test Question"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.testQuestion}
          onChange={handleChange}
        />
        <Box className={classes.dropzoneContainer}>
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <Typography variant="body2" className={classes.fileUpload}>
              <FileUpload className={classes.fileUploadIcon} />
              Drag & drop to upload JSON and/or wav files or browse for files.
            </Typography>
          </div>
          <List>
            {sortedFileArray.map((fileWrapper) => (
              <Box key={fileWrapper.id} className={classes.fileList}>
                <ListItem key={fileWrapper.id}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)]?.status === 'Inactive' ? (
                          <Cancel className={classes.inactiveIcon} />
                        ) : (
                          statusIcons[fileWrapper.status]
                        )}

                        <Typography
                          variant="body2"
                        >
                          {fileWrapper.file.name}
                        </Typography>
                      </Typography>
                    }
                    secondary={
                      fileWrapper.status === 'error' ? (
                        <Typography
                          variant="h6"
                          sx={{ color: 'red', marginLeft: '20px' }}
                        >
                          {fileWrapper.error}
                        </Typography>
                      ) : fileWrapper.status === 'uploading' ? (
                        <Typography
                          variant="h6"
                          sx={{ color: 'grey.500', marginLeft: '20px' }}
                        >
                          Loading...
                        </Typography>
                        ) : (
                          formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)]?.description && (
                            <Tooltip title={formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)]?.description}>
                              <Typography
                                variant="body2"
                                className={classes.truncate}
                              >
                                {formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)]?.description}
                              </Typography>
                            </Tooltip>
                          )
                        )
                    }
                  />

                  {fileWrapper.status === 'error' ? (
                    <IconButton onClick={() => handleRetry(fileWrapper.id)}>
                      <Replay fontSize='small' />
                    </IconButton>
                  ) : (
                    <>
                      {fileWrapper.status === 'success' && fileWrapper.file.name !== 'Custom' && (
                          <>
                            <IconButton onClick={() => handleFilesToggleStatus(files.findIndex((file) => file.id === fileWrapper.id))}>
                              {formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)].status === 'Active' ? (
                                <ToggleOn className={classes.activeToggle} />
                              ) : (
                                <ToggleOff className={classes.inactiveToggle} />
                              )}
                            </IconButton>
                            <IconButton onClick={() => handleOpenEditFileModal(fileWrapper.id)}>
                                <Edit fontSize='small' />
                            </IconButton>
                            <IconButton onClick={() => handleDownload(fileWrapper.id)}>
                              {downloadingFiles[fileWrapper.id] ? (
                                <Downloading fontSize='small' />
                              ) : (
                                <Download fontSize='small' />
                              )}
                            </IconButton>
                          </>
                        )}
                        <ToastContainer />
                        <IconButton onClick={() => handleDelete(fileWrapper.id)}>
                          <Delete fontSize='small' />
                      </IconButton>
                    </>
                  )}
                </ListItem>
                <Modal
                  key={fileWrapper.id}
                  open={openEditFileModal[fileWrapper.id] || false}
                  onClose={() => handleCloseEditFileModal(fileWrapper.id)}
                  closeAfterTransition
                  className={classes.modalContainer}
                >
                  <div className={classes.modalContent}>
                    <TextField
                      label="Filename"
                      value={newFileNames[fileWrapper.id] || ''}
                      onChange={(e) => handleFileNameChange(fileWrapper.id, e as React.ChangeEvent<HTMLInputElement>)}
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="File Description"
                      value={newFileDescriptions[fileWrapper.id] !== undefined ? newFileDescriptions[fileWrapper.id] : formData.conversationDetails[files.findIndex((file) => file.id === fileWrapper.id)]?.description || ''}
                      onChange={(e) => handleDescriptionChange(fileWrapper.id, e as React.ChangeEvent<HTMLInputElement>)}
                      fullWidth
                      margin="normal"
                      multiline
                      rows={4}
                    />
                    <div className={classes.buttonContainer}>
                      <Button type="submit" variant="contained" color="primary" size="small" onClick={() => handleRenameSave(fileWrapper.id)}>
                        Save
                      </Button>
                      <Button variant="contained" color="primary" size="small" onClick={() => handleCloseEditFileModal(fileWrapper.id)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Modal>
              </Box>
            ))}
          </List>
        </Box>

        <div className={classes.buttonContainer}>
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
          <Button variant="contained" color="primary" onClick={handleTestExpert}>
            Test Demo
          </Button>

          <Modal
            open={openModal}
            onClose={handleModalClose}
            closeAfterTransition
            className={classes.modalContainer}
          >
            <div className={`${classes.modalContent} ${getModalContentClass()}`}>
              <IconButton
                onClick={handleModalClose}
                className={classes.closeButton}
              >
                <Close />
              </IconButton>
              <h3>{formData.testQuestion}</h3>
              {errorMessage ? (
                <p>{errorMessage}</p>
              ) : (
                answers.map((chunk, index) => (
                  <Accordion key={index}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                      <Typography>Answer {index + 1}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>{chunk}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </div>
          </Modal>
        </div>
      </form>
    </Container>
  );
}

export default VerticalForm;